import { useState, useRef, useEffect } from "react";

import { Draggable } from "react-beautiful-dnd";
import { Pagination } from "react-bootstrap";
import { Stack, Typography } from "@mui/material";

const RoundTab = ({ 
  id, 
  index, 
  name, 
  isActive, 
  isDraggable, 
  onPageChange, 
  onDuplicate, 
  onDelete, 
  canDelete,
  onHover,
  onLeave,
  type
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const containerRef = useRef(null);

  // Handle click outside to close menu
  useEffect(() => {
    function handleClickOutside(event) {
      // Close menu if clicking anywhere outside the round tab container
      if (isMenuOpen) {
        const isClickInsideContainer = containerRef.current && containerRef.current.contains(event.target);
        
        if (!isClickInsideContainer) {
          setIsMenuOpen(false);
        }
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (onHover) onHover(index);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (onLeave) onLeave();
  };

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsMenuOpen(!isMenuOpen);
  };

  const handlePageClick = () => {
    // Close menu when clicking on the page item
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
    onPageChange(id);
  };

  const handleDuplicate = (e) => {
    e.stopPropagation();
    onDuplicate(index);
    setIsMenuOpen(false);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (canDelete) {
      onDelete(index);
      setIsMenuOpen(false);
    }
  };

  const pageItem = (
    <Pagination.Item 
      active={isActive} 
      onClick={handlePageClick}
    >
      <Stack alignItems="center">
        <Typography variant="body1" fontWeight="bold">{name ?? `Round ${id + 1}`}</Typography>
        <small>({type})</small>
      </Stack>
    </Pagination.Item>
  );

  if (!isDraggable) {
    return pageItem;
  }

  return (
    <div 
      ref={containerRef}
      className="round-tab-wrapper"
      style={{ 
        display: 'inline-block', 
        position: 'relative',
        marginRight: '4px',
        textAlign: 'center'
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Draggable draggableId={`round-${id}`} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{
              ...provided.draggableProps.style,
              display: 'inline-block'
            }}
          >
            {pageItem}
          </div>
        )}
      </Draggable>
      
      {/* Menu button - only show when hovered */}
      {isHovered && (
        <div
          style={{ 
            position: 'absolute',
            top: '-5px',
            right: '-5px',
            zIndex: 1050
          }}
        >
          <button
            ref={buttonRef}
            style={{ 
              padding: '1px 4px', 
              fontSize: '8px',
              lineHeight: '1',
              border: 'none',
              background: 'rgba(108, 117, 125, 0.9)',
              color: 'white',
              minWidth: 'auto',
              width: '16px',
              height: '16px',
              borderRadius: '2px',
              cursor: 'pointer'
            }}
            onClick={handleMenuToggle}
            onMouseDown={(e) => e.stopPropagation()}
          >
            ⋯
          </button>
        </div>
      )}

      {/* Dropdown menu */}
      {isMenuOpen && (
        <div
          ref={menuRef}
          style={{
            position: 'absolute',
            top: '20px',
            right: '0px',
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            minWidth: '120px',
            zIndex: 1060
          }}
        >
          <div
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderBottom: '1px solid #eee'
            }}
            onClick={handleDuplicate}
            onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
            onMouseOut={(e) => e.target.style.background = 'white'}
          >
            📋 Duplicate
          </div>
          <div
            style={{
              padding: '8px 12px',
              cursor: canDelete ? 'pointer' : 'not-allowed',
              color: canDelete ? '#dc3545' : '#6c757d'
            }}
            onClick={handleDelete}
            onMouseOver={(e) => {
              if (canDelete) e.target.style.background = '#f8f9fa';
            }}
            onMouseOut={(e) => e.target.style.background = 'white'}
          >
            🗑️ Delete
          </div>
        </div>
      )}
    </div>
  );
};

export default RoundTab; 